import { useEffect, useState } from "react";
import { CheckCircle2, XCircle } from "lucide-react";
import { Button } from "./ui/button";
import { DifficultyBadge } from "./bits";
import { API } from "../lib/api";
import { TOPIC_NAMES } from "../lib/api";

const LETTERS = ["A", "B", "C", "D", "E"];

export function DemoQuestion() {
  const [q, setQ] = useState(null);
  const [selected, setSelected] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setSelected(null);
    setSubmitted(false);
    try {
      const { data } = await API.get("/demo/question");
      setQ(data);
    } catch { setQ(null); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="py-16 text-center text-muted-foreground text-sm" data-testid="demo-loading">Loading demo question…</div>;
  if (!q) return <div className="py-16 text-center text-muted-foreground text-sm">Demo question unavailable.</div>;

  const correct = submitted && selected === q.correct_answer;

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm p-6 md:p-10" data-testid="demo-question-card">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <span className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Sample SBA</span>
        <span className="text-xs text-muted-foreground">·</span>
        <span className="text-xs font-medium text-muted-foreground">{TOPIC_NAMES[q.topic] || q.topic}</span>
        <DifficultyBadge level={q.difficulty} />
      </div>
      <p className="text-base md:text-lg leading-relaxed font-medium" data-testid="demo-stem">{q.stem}</p>
      <div className="mt-7 space-y-3">
        {LETTERS.map((l) => {
          const isSel = selected === l;
          let cls = "border-border hover:border-primary/50 hover:bg-muted/50";
          let icon = null;
          if (!submitted && isSel) cls = "border-primary bg-secondary ring-1 ring-primary";
          if (submitted) {
            if (l === q.correct_answer) { cls = "border-success bg-success/10"; icon = <CheckCircle2 className="w-5 h-5 text-success shrink-0" />; }
            else if (isSel) { cls = "border-destructive bg-destructive/10"; icon = <XCircle className="w-5 h-5 text-destructive shrink-0" />; }
            else cls = "border-border opacity-70";
          }
          return (
            <button key={l} type="button" data-testid={`demo-option-${l}`} disabled={submitted}
              onClick={() => setSelected(l)}
              className={`w-full text-left flex items-start gap-4 px-5 py-4 rounded-lg border transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${cls}`}>
              <span className={`w-7 h-7 rounded-full border flex items-center justify-center text-sm font-bold shrink-0 ${isSel && !submitted ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{l}</span>
              <span className="text-sm md:text-base leading-relaxed flex-1">{q.options[l]}</span>
              {icon}
            </button>
          );
        })}
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        {!submitted && (
          <Button data-testid="demo-submit-btn" disabled={!selected} onClick={() => setSubmitted(true)} className="px-8">
            Submit answer
          </Button>
        )}
      </div>
      {submitted && (
        <div className="mt-6 space-y-5 animate-fade-up" data-testid="demo-explanation">
          <div className={`flex items-center gap-2.5 px-4 py-3 rounded-md border font-semibold text-sm ${correct ? "bg-success/10 border-success/30 text-success" : "bg-destructive/10 border-destructive/30 text-destructive"}`}>
            {correct ? <><CheckCircle2 className="w-5 h-5" /> Correct.</> : <><XCircle className="w-5 h-5" /> Incorrect — the correct answer is {q.correct_answer}.</>}
          </div>
          <div className="bg-background border border-border rounded-lg p-5">
            <h4 className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-2">Why {q.correct_answer} is correct</h4>
            <p className="text-sm leading-relaxed">{q.explanation_correct}</p>
          </div>
          <div className="bg-background border border-border rounded-lg p-5 space-y-3">
            <h4 className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Why the other options are wrong</h4>
            {LETTERS.filter((l) => l !== q.correct_answer).map((l) => (
              <p key={l} className="text-sm leading-relaxed" data-testid={`demo-explanation-${l}`}>
                <span className={`font-semibold ${l === selected ? "text-destructive" : ""}`}>{l}{l === selected ? " (your answer)" : ""}:</span>{" "}
                <span className="text-muted-foreground">{q.option_explanations?.[l]}</span>
              </p>
            ))}
          </div>
          <div className="bg-secondary border border-border rounded-lg p-5">
            <h4 className="text-xs tracking-[0.15em] uppercase font-semibold text-primary mb-1">Key learning point</h4>
            <p className="text-sm leading-relaxed">{q.key_learning_point}</p>
            <p className="mt-2 text-xs text-muted-foreground">Reference: {q.reference}</p>
          </div>
        </div>
      )}
    </div>
  );
}
